// assets/conditional-variants.js

document.addEventListener('DOMContentLoaded', async () => {
    const FRAME_OPTION_NAME = 'Framing Options';
    const SIZE_OPTION_NAME = 'Size';
    // Matches "Orientation" as well as the older "Orientation/Border" and "Orientation/Matboard"
    const ORIENTATION_OPTION_PREFIX = 'Orientation';

    const DEBUG = false;
    const log  = (...a) => DEBUG && console.log('[cv:d]', ...a);
    const warn = (...a) => DEBUG && console.warn('[cv:d]', ...a);

    const handle = window.location.pathname.split('/products/')[1]?.split('/')[0];
    if (!handle) return;

    let productData;
    try {
        const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
        productData = await res.json();
    } catch (e) {
        console.error('[cv:d] Failed to load product JSON:', e);
        return;
    }

    const variants = productData?.variants || [];

    const mediaIdByVariantId = new Map();
    for (const v of variants) {
    const mid =
        v?.featured_media?.id ||
        v?.featured_media_id ||
        null;

    if (mid) mediaIdByVariantId.set(String(v.id), mid);
    }

    const optionNames = Array.isArray(productData?.options)
    ? (typeof productData.options[0] === 'string'
        ? productData.options
        : productData.options.map(o => o.name))
    : [];

    if (!variants.length || !optionNames.length) {
        console.error('[cv:d] Missing variants/options in product JSON.');
        return;
    }

    const frameIdx = optionNames.indexOf(FRAME_OPTION_NAME);
    const sizeIdx  = optionNames.indexOf(SIZE_OPTION_NAME);
    const oriIdx = optionNames.findIndex(n => n.startsWith(ORIENTATION_OPTION_PREFIX));

    if (frameIdx === -1 || sizeIdx === -1 || oriIdx === -1) {
        warn('Option names not found in product options:', { optionNames });
        return;
    }

    const getOpt = (v, idx) => v?.[`option${idx + 1}`] ?? (Array.isArray(v?.options) ? v.options[idx] : null);

    const sizesByFrame = new Map();
    const orisByFrameSize = new Map();
    const variantByTriple = new Map();

    for (const v of variants) {
        if (!(v.available ?? true)) continue;

        const f = getOpt(v, frameIdx);
        const s = getOpt(v, sizeIdx);
        const o = getOpt(v, oriIdx);
        if (!f || !s || !o) continue;

        if (!sizesByFrame.has(f)) sizesByFrame.set(f, new Set());
        sizesByFrame.get(f).add(s);

        const fsKey = `${f}||${s}`;
        if (!orisByFrameSize.has(fsKey)) orisByFrameSize.set(fsKey, new Set());
        orisByFrameSize.get(fsKey).add(o);

        const fsoKey = `${f}||${s}||${o}`;
        variantByTriple.set(fsoKey, v);
    }

    function firstFromSet(set) {
        if (!set) return null;
        for (const x of set) return x;
        return null;
    }

    // Smallest paper size first (A4 up to A0); other sizes keep the product's own order
    const SIZE_ORDER = ['A4', 'A3', 'A2', 'A1', 'A0'];
    function smallestSize(set) {
        if (!set) return null;
        for (const prefix of SIZE_ORDER) {
            for (const s of set) if (s.startsWith(prefix)) return s;
        }
        return firstFromSet(set);
    }

    function findChangeVariantHost() {
        const seed =
            document.querySelector('.product__block__variants') ||
            document.querySelector('.form__selectors') ||
            document.querySelector('[data-popout]') ||
            document.querySelector('[data-popout-toggle]') ||
            document.querySelector('form[action*="/cart/add"]');

        if (!seed) return null;
        return seed.closest('[x-data]') || seed;
        }

        function getAlpineData(el) {
        if (window.Alpine && typeof window.Alpine.$data === 'function') {
            try { return window.Alpine.$data(el); } catch (_) {}
        }
        return el?.__x?.$data || null;
    }

    function getChangeVariantFn() {
        if (typeof window.changeVariant === 'function') return { fn: window.changeVariant, ctx: window };

        const host = findChangeVariantHost();
        if (!host) return null;

        const data = getAlpineData(host);
        if (data && typeof data.changeVariant === 'function') return { fn: data.changeVariant, ctx: data };

        const parent = host.parentElement?.closest?.('[x-data]');
        if (parent) {
            const parentData = getAlpineData(parent);
            if (parentData && typeof parentData.changeVariant === 'function') return { fn: parentData.changeVariant, ctx: parentData };
        }

        return null;
    }

    function getPreloadVariantFn() {
        if (typeof window.preloadVariant === 'function') return { fn: window.preloadVariant, ctx: window };

        const host = findChangeVariantHost();
        if (!host) return null;

        const data = getAlpineData(host);
        if (data && typeof data.preloadVariant === 'function') return { fn: data.preloadVariant, ctx: data };

        const parent = host.parentElement?.closest?.('[x-data]');
        if (parent) {
            const parentData = getAlpineData(parent);
            if (parentData && typeof parentData.preloadVariant === 'function') return { fn: parentData.preloadVariant, ctx: parentData };
        }

        return null;
        }

    function callPreloadVariant(variantId) {
        const found = getPreloadVariantFn();
        if (!found) {
            warn('Could not locate preloadVariant() in Alpine scope.');
            return false;
        }

        const variantUrl = `${window.location.pathname}?variant=${variantId}`;

        try {
            found.fn.call(found.ctx, variantUrl);
            log('Preloaded variant', { variantId, variantUrl });
            return true;
        } catch (e) {
            warn('preloadVariant call failed', e);
            return false;
        }
        }

    function callChangeVariant(variantId, featuredMediaId = null) {
        const found = getChangeVariantFn();
        if (!found) {
            console.error('[cv:d] Could not locate changeVariant() in Alpine scope.');
            return false;
        }

        const url = `${window.location.origin}${window.location.pathname}?variant=${variantId}`;
        log('Calling changeVariant', { url, variantId, featuredMediaId });

        try {
            found.fn.call(found.ctx, url, variantId, featuredMediaId);
            return true;
        } catch (e) {
            console.error('[cv:d] changeVariant call failed:', e);
            return false;
        }
    }

    function getHidden(optionName) {
        return document.querySelector(`input[data-popout-input][name="options[${optionName}]"]`)
            || document.querySelector(`input[name="options[${optionName}]"][type="hidden"]`);
    }

    const cssString = (v) => String(v).replace(/["\\]/g, '\\$&');
    const ORIENTATION_OPTION_NAME = optionNames[oriIdx];

    // An option is either a dropdown (hidden input) or a row of buttons (radio inputs)
    function currentValue(optionName) {
        const radio = document.querySelector(`input[type="radio"][name="options[${cssString(optionName)}]"]:checked`);
        if (radio) return radio.value;

        const hidden = getHidden(optionName);
        return hidden ? hidden.value : null;
    }

    if ([FRAME_OPTION_NAME, SIZE_OPTION_NAME, ORIENTATION_OPTION_NAME].some(name => currentValue(name) === null)) {
        warn('Missing one or more option inputs.', { optionNames });
        return;
    }

    // Options are hidden with a generated style rule rather than by editing the
    // list itself, because the theme redraws the list after every choice.
    const hideStyle = document.createElement('style');
    hideStyle.setAttribute('data-conditional-variants', '');
    document.head.appendChild(hideStyle);

    const allValuesByName = new Map();
    (productData.options || []).forEach((o, i) => {
        allValuesByName.set(optionNames[i], Array.isArray(o?.values) ? o.values : []);
    });

    function hideRulesFor(optionName, allowedSet) {
        const wrapper = `.selector-wrapper[data-select-label="${cssString(optionName)}"]`;
        return (allValuesByName.get(optionName) || [])
            .filter(v => !allowedSet.has(v))
            .flatMap(v => [
                `${wrapper} li:has(> [data-popout-option][data-value="${cssString(v)}"])`,
                `${wrapper} :is(.tt-frame, .tt-size, .radio__button):has(> input[value="${cssString(v)}"])`
            ]);
    }

    let applyTimer = null;
    function scheduleApplyFilters(reason = 'unknown') {
        window.clearTimeout(applyTimer);
        applyTimer = window.setTimeout(() => applyFilters(reason), 30);
    }

    function applyFilters(reason = 'unknown') {
        const frame = currentValue(FRAME_OPTION_NAME);
        const size  = currentValue(SIZE_OPTION_NAME);

        const allowedFrames = new Set(sizesByFrame.keys());
        const allowedSizes = sizesByFrame.get(frame) || new Set();
        const allowedOris = orisByFrameSize.get(`${frame}||${size}`) || new Set();

        const selectors = [
            ...hideRulesFor(FRAME_OPTION_NAME, allowedFrames),
            ...hideRulesFor(SIZE_OPTION_NAME, allowedSizes),
            // Leave the orientation list alone while the frame/size pair is mid-change
            ...(allowedOris.size ? hideRulesFor(ORIENTATION_OPTION_NAME, allowedOris) : [])
        ];

        const css = selectors.length ? `${selectors.join(',\n')} { display: none; }` : '';
        if (hideStyle.textContent !== css) hideStyle.textContent = css;

        log('Applied filters', {
            reason,
            frame,
            size,
            allowedSizes: allowedSizes.size,
            allowedOris: allowedOris.size
        });
    }

    let inFlight = false;

    function selectBestVariantForFrame(frame, size, ori) {
        const allowedSizes = sizesByFrame.get(frame);
        if (!allowedSizes || allowedSizes.size === 0) return null;

        const chosenSize = allowedSizes.has(size) ? size : smallestSize(allowedSizes);
        if (!chosenSize) return null;

        const allowedOris = orisByFrameSize.get(`${frame}||${chosenSize}`) || new Set();
        if (allowedOris.size === 0) return null;

        const chosenOri = allowedOris.has(ori) ? ori : firstFromSet(allowedOris);
        if (!chosenOri) return null;

        const v = variantByTriple.get(`${frame}||${chosenSize}||${chosenOri}`);
        return v?.id || null;
        }

    function selectFirstValidForFrame(frame) {
        const allowedSizes = sizesByFrame.get(frame);
        const firstSize = firstFromSet(allowedSizes);
        if (!firstSize) return null;

        const allowedOris = orisByFrameSize.get(`${frame}||${firstSize}`);
        const firstOri = firstFromSet(allowedOris);
        if (!firstOri) return null;

        const v = variantByTriple.get(`${frame}||${firstSize}||${firstOri}`);
        return v?.id || null;
    }

    const preloaded = new Set();
    let preloadQueue = [];
    let draining = false;

    function enqueuePreload(variantId) {
    if (!variantId) return;
    const key = String(variantId);
    if (preloaded.has(key)) return;

    preloaded.add(key);
    preloadQueue.push(variantId);
    drainPreloads();
    }

    function drainPreloads() {
    if (draining) return;
    draining = true;

    const runOne = () => {
        const next = preloadQueue.shift();
        if (!next) {
        draining = false;
        return;
        }

        callPreloadVariant(next);

        setTimeout(() => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(runOne, { timeout: 800 });
        } else {
            runOne();
        }
        }, 80);
    };

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(runOne, { timeout: 800 });
    } else {
        setTimeout(runOne, 0);
    }
    }

    function ensureOrientationValid(frame, size, ori) {
        const allowedOris = orisByFrameSize.get(`${frame}||${size}`) || new Set();
        if (allowedOris.has(ori)) return null;

        const firstOri = firstFromSet(allowedOris);
        if (!firstOri) return null;

        const v = variantByTriple.get(`${frame}||${size}||${firstOri}`);
        return v?.id || null;
    }

    function requestVariant(variantId, reason) {
        if (!variantId) return;
        if (inFlight) return;

        inFlight = true;

        const featuredMediaId = mediaIdByVariantId.get(String(variantId)) || null;
        const ok = callChangeVariant(variantId, featuredMediaId);

        window.setTimeout(() => {
            inFlight = false;
            scheduleApplyFilters(`after changeVariant (${reason})`);
        }, 150);

        log('Requested variant', { variantId, reason, ok });
    }

    let lastFrame = currentValue(FRAME_OPTION_NAME);
    let lastSize  = currentValue(SIZE_OPTION_NAME);
    let lastOri   = currentValue(ORIENTATION_OPTION_NAME);

    function handleAnyChange(source) {
        const frame = currentValue(FRAME_OPTION_NAME);
        const size  = currentValue(SIZE_OPTION_NAME);
        const ori   = currentValue(ORIENTATION_OPTION_NAME);

        scheduleApplyFilters(`selection change (${source})`);

        if (frame !== lastFrame) {
        lastFrame = frame;

        // The theme switches variant by itself when the combination exists
        if (variantByTriple.has(`${frame}||${size}||${ori}`)) return;

        const nextVariantId = selectBestVariantForFrame(frame, size, ori);
        requestVariant(nextVariantId, 'frame changed (preserve size/ori)');
        return;
        }

        if (size !== lastSize) {
            lastSize = size;

            const fixVariantId = ensureOrientationValid(frame, size, ori);
            requestVariant(fixVariantId, 'size changed (fix ori)');
            return;
        }

        if (ori !== lastOri) {
            lastOri = ori;
            return;
        }
    }

    let changeTimer = null;
    function scheduleHandleChange(source) {
        window.clearTimeout(changeTimer);
        changeTimer = window.setTimeout(() => handleAnyChange(source), 30);
    }

    const variantsRoot = document.querySelector('.product__block__variants') || document.body;
    variantsRoot.addEventListener('change', () => scheduleHandleChange('change'));
    new MutationObserver(() => scheduleHandleChange('variants DOM changed'))
    .observe(variantsRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'aria-current', 'checked'] });

    document.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-popout-toggle]');
        if (!btn) return;
        scheduleApplyFilters('popout opened');
    }, true);

    applyFilters('init');

    function preloadFirstValidForAllFrames(reason = 'init') {
        for (const frame of sizesByFrame.keys()) {
            const vid = selectFirstValidForFrame(frame);
            enqueuePreload(vid);
        }
        log('Queued preloads for frames', { reason, frames: sizesByFrame.size });
    }

    setTimeout(() => preloadFirstValidForAllFrames('post-load'), 300);

    log('Preload found?', { preloadVariantFound: !!getPreloadVariantFn() });

    log('Initialized', {
        optionNames,
        initial: {
            frame: currentValue(FRAME_OPTION_NAME),
            size: currentValue(SIZE_OPTION_NAME),
            ori: currentValue(ORIENTATION_OPTION_NAME)
        },
        changeVariantFound: !!getChangeVariantFn()
    });
});
